import { useTranslation } from "react-i18next";
import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { apollo } from "../../../../utils/apollo";
import { GET_FILE_IMPORT_LIST, REPROCESS_FILE_IMPORT } from "../../../../utils/graphql";
import PaginationFull from "../../../../components/PaginationFull";
import useIntegration from "../../../../utils/useIntegration";
import {
  InformationCircleIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  ChartBarIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import IconButton from "../../../../components/IconButton";
import { cn } from "../../../../utils/style";
import { toast } from "react-toastify";

function PolicyImportView({ filterStatus, onViewResults }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const { integrationUuid, integration } = useIntegration();

  const reprocessMutation = useMutation(
    async (fileImportUuid) => {
      const { data } = await apollo.mutate({
        mutation: REPROCESS_FILE_IMPORT,
        variables: { fileImportUuid },
      });
      return data?.reprocessFileImport;
    },
    {
      onSuccess: () => {
        toast.success(t("Reprocessing started successfully"));
        queryClient.invalidateQueries("policy_import_processing_view_list");
        queryClient.invalidateQueries("file_import_status_count");
      },
      onError: (error) => {
        toast.error(error.message || t("Failed to reprocess file"));
      },
    },
  );

  const handleReprocess = (fileImportUuid) => {
    if (window.confirm(t("Are you sure you want to reprocess this file?"))) {
      reprocessMutation.mutate(fileImportUuid);
    }
  };

  const fetchFileImportProcessing = useQuery({
    queryKey: [
      "policy_import_processing_view_list",
      {
        page,
        filterStatus,
        integrationUuid,
      },
    ],
    queryFn: async () => {
      const { data } = await apollo.query({
        query: GET_FILE_IMPORT_LIST,
        variables: {
          page,
          first: 15,
          filter: {
            status: filterStatus || null,
            integrationUuid: integrationUuid || null,
          },
        },
      });
      return data?.getFileImportList;
    },
    keepPreviousData: false,
    refetchInterval: (data) => {
      const isProcessing = data?.data?.some((i) => i.status === "processing");
      return isProcessing ? 10000 : false;
    },
    onSuccess: () => {
      queryClient.invalidateQueries("file_import_status_count");
    },
  });
  const fileImports = useMemo(
    () =>
      fetchFileImportProcessing?.isSuccess
        ? fetchFileImportProcessing?.data
        : null,
    [fetchFileImportProcessing],
  );

  const statusBadge = (status) => {
    const map = {
      processing: "bg-amber-50 text-amber-700 border-amber-200",
      pending: "bg-amber-50 text-amber-700 border-amber-200",
      completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
      failed: "bg-rose-50 text-rose-700 border-rose-200",
    };
    return map[status] || "bg-slate-50 text-slate-700 border-slate-200";
  };

  return (
    <div className="mt-8 space-y-6">
      {filterStatus === "completed" && (
        <div className="flex gap-4 p-5 rounded-2xl border border-blue-100 bg-blue-50/50 shadow-sm transition-all duration-300">
          <div className="flex-shrink-0">
            <InformationCircleIcon className="h-6 w-6 text-blue-500" />
          </div>
          <p className="text-sm font-medium leading-relaxed text-blue-800">
            {t(
              "In this tab, you can find the files that were uploaded successfully to {{integrationName}}, along with the successfully added members list, members that have been added because of errors, and the members that have been rejected by ICP. Members with errors should be uploaded again.",
              { integrationName: integration?.name || "the integration" }
            )}
          </p>
        </div>
      )}
      {filterStatus === "failed" && (
        <div className="flex gap-4 p-5 rounded-2xl border border-amber-100 bg-amber-50/50 shadow-sm transition-all duration-300">
          <div className="flex-shrink-0">
            <ExclamationTriangleIcon className="h-6 w-6 text-amber-500" />
          </div>
          <p className="text-sm font-medium leading-relaxed text-amber-800">
            {t(
              "In this tab, you can find the files that failed to be uploaded to {{integrationName}} due to validation errors in the excel file. All members in these files have not been added to {{integrationName}} system; therefore, validation errors should be fixed before trying to upload the file again.",
              { integrationName: integration?.name || "the integration" }
            )}
          </p>
        </div>
      )}

      <div className="flow-root">
        <div className="overflow-auto pb-16 rounded-2xl border border-slate-200/60 bg-white shadow-sm">
          <div className="inline-block min-w-full align-middle">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-50/50">
                <tr>
                  <th
                    scope="col"
                    className="px-4 py-4 text-start text-[11px] font-black uppercase tracking-wider text-slate-500"
                  >
                    {t("File UUID")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-start text-sm font-semibold text-gray-900"
                  >
                    {t("integration")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-start text-sm font-semibold text-gray-900"
                  >
                    {t("total")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-start text-sm font-semibold text-gray-900"
                  >
                    {t("success")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-start text-sm font-semibold text-gray-900"
                  >
                    {t("failed")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-start text-sm font-semibold text-gray-900"
                  >
                    {t("status")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-start text-sm font-semibold text-gray-900"
                  >
                    {t("uploaded at")}
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3.5 text-start text-sm font-semibold text-gray-900"
                  >
                    {t("actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {fileImports?.data?.map((fileImport) => (
                  <tr
                    key={fileImport.fileImportUuid}
                    className="hover:bg-slate-50/40 transition-colors"
                  >
                    <td className="whitespace-nowrap px-3 py-4 text-xs font-mono text-slate-500">
                      {fileImport.fileImportUuid}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <div className="flex flex-col gap-0.5">
                        <span className="font-medium text-slate-700">
                          {fileImport.integration?.name}
                        </span>
                        {fileImport.integration?.integrationType && (
                          <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">
                            {fileImport.integration.integrationType}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 font-medium">
                      {fileImport.totalRecords}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-emerald-600 font-bold">
                      {fileImport.importedRecords}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-rose-600 font-bold">
                      {fileImport.failedRecords}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <div className="flex flex-col gap-1 min-w-[140px]">
                        <span
                          className={cn(
                            "capitalize font-bold text-xs px-2.5 py-0.5 rounded-full border inline-flex w-fit",
                            statusBadge(fileImport.status),
                          )}
                        >
                          {t(fileImport.status)}
                        </span>
                        {fileImport.status === "processing" && (
                          <div className="w-full bg-gray-100 rounded-full h-1.5 mt-1 overflow-hidden border border-slate-100">
                            <div
                              className="bg-primary-500 h-1.5 rounded-full transition-all duration-500 shadow-[0_0_8px_rgba(59,130,246,0.4)]"
                              style={{
                                width: `${Math.min(100, Math.max(5, (fileImport.importedRecords / fileImport.totalRecords) * 100))}%`,
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {fileImport.startedAt
                        ? new Date(fileImport.startedAt).toLocaleString()
                        : "-"}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        {/* Reprocess button - for failed imports with 0 records */}
                        {fileImport.status === "failed" &&
                          parseInt(fileImport.totalRecords) === 0 && (
                            <IconButton
                              onClick={() =>
                                handleReprocess(fileImport.fileImportUuid)
                              }
                              title={t("Reprocess File")}
                              isLoading={
                                reprocessMutation.isLoading &&
                                reprocessMutation.variables ===
                                fileImport.fileImportUuid
                              }
                            >
                              <ArrowPathIcon className="w-5 h-5 text-amber-500" />
                            </IconButton>
                          )}
                        {/* View Results — only for Shafafiya/COC and SlashData imports */}
                        {["shafafiya", "slashdata"].includes(
                          fileImport.integration?.integrationType,
                        ) &&
                          onViewResults && (
                            <IconButton
                              onClick={() => onViewResults(fileImport)}
                              title={t("View Results")}
                            >
                              <ChartBarIcon className="w-5 h-5 text-primary-500" />
                            </IconButton>
                          )}
                        {/* Download error excel (for failed files) */}
                        {fileImport.errorFilepath && (
                          <IconButton
                            onClick={() => {
                              window.open(
                                `${process.env.REACT_APP_GAID_API_BASE_URL}/download-import-errors/${fileImport.fileImportUuid}`,
                                "_blank",
                              );
                            }}
                            title={t("Download Error Excel")}
                          >
                            <ArrowDownTrayIcon className="w-5 h-5 text-rose-500" />
                          </IconButton>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {fileImports?.data && !fileImports?.data?.length && (
              <div className="flex items-center justify-center h-40 text-gray-500 font-light text-xl">
                {t("No file imports found")}
              </div>
            )}
          </div>
        </div>
      </div>
      <PaginationFull
        paginator={fileImports?.paginatorInfo}
        get={page}
        set={setPage}
      />
    </div>
  );
}
export default PolicyImportView;
